
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from './components/Layout';
import { Landing } from './components/Landing';
import { Auth } from './components/Auth';
import { Welcome } from './components/Welcome';
import { ProjectSetup } from './components/ProjectSetup';
import { OnboardingStep2 } from './components/OnboardingStep2';
import { OnboardingStep3 } from './components/OnboardingStep3';
import { OnboardingStep4 } from './components/OnboardingStep4';
import { StagesSetup } from './components/StagesSetup';
import { StatsView } from './components/StatsView';
import { ExpenseModal } from './components/ExpenseModal';
import { DashboardView } from './components/DashboardView';
import { ExpensesView } from './components/ExpensesView';
import { PhotoGallery } from './components/PhotoGallery';
import { SettingsView } from './components/SettingsView';
import { ProviderView } from './components/ProviderView';
import { ProjectList } from './components/ProjectList'; 
import { PhotoUploadModal } from './components/PhotoUploadModal';
import { OnboardingTutorial } from './components/OnboardingTutorial';
import { SmartNotification } from './components/SmartNotification';
import { Project, Expense, ExpenseCategory, Photo, ProjectType, PlanTier, BudgetMode } from './types';
import { EMPTY_PROJECT, CONSTRUCTION_STAGES, RENOVATION_STAGES, STRIPE_PLANS, APP_CONFIG } from './constants';
import { auth } from './services/firebase';
import { onAuthStateChanged, signOut } from '@firebase/auth';
import { getUserProjects, saveUserProject, updateUserProject, getProjectByPhotoToken, getUserSubscription } from './services/databaseService';
import { createCheckoutSession } from './services/stripeService';
import { compressImage } from './services/imageService';
import { toLocalISODate } from './utils/formatters';
import { Link as LinkIcon, Trash } from 'lucide-react';
import { PricingModal } from './components/PricingModal';

export const App: React.FC = () => {
  type View = 'landing' | 'auth' | 'welcome' | 'projects-list' | 'onboarding-1' | 'onboarding-2' | 'onboarding-3' | 'onboarding-4' | 'stages-setup' | 'app' | 'provider-view' | 'error-link';
  
  const [view, setView] = useState<View>('landing');
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  const [projectsList, setProjectsList] = useState<Project[]>([]);
  const [project, setProject] = useState<Project>(EMPTY_PROJECT); 
  
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isProviderMode, setIsProviderMode] = useState(false);
  const [inviteCodeFromUrl, setInviteCodeFromUrl] = useState<string | null>(null);
  
  const [planTier, setPlanTier] = useState<PlanTier>('trial');
  const [isTrial, setIsTrial] = useState(false); 
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [tempPhotoUrl, setTempPhotoUrl] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => {
      const newVal = !prev;
      if (typeof document !== 'undefined') {
        if (newVal) document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
      }
      return newVal;
    });
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let unsubscribeAuth: () => void;

    const initApp = async () => {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const invite = params.get('invite');

      if (invite) setInviteCodeFromUrl(invite);

      if (token) {
        try {
          const providerProject = await getProjectByPhotoToken(token);
          if (providerProject) {
            setProject(providerProject);
            setIsProviderMode(true);
            setView('provider-view');
            setIsLoading(false);
            return; 
          } else {
            setGlobalError("Link expirado ou inválido.");
            setView('error-link');
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error("[App] Provider token error:", error);
          setGlobalError("Erro ao validar acesso.");
          setView('error-link');
          setIsLoading(false);
          return;
        }
      }

      unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        try {
          if (user && user.emailVerified) {
            const sub = await getUserSubscription(user.uid);
            let tier: PlanTier = 'trial';
            let trialActive = false;
            
            const creationTime = user.metadata.creationTime;
            const creationMs = creationTime ? new Date(creationTime).getTime() : new Date().getTime();
            const trialLimitMs = APP_CONFIG.TRIAL_PERIOD_DAYS * 24 * 60 * 60 * 1000;
            const isWithinTrial = (new Date().getTime() - creationMs) < trialLimitMs;

            if (sub) {
                const priceId = sub.items?.[0]?.plan?.id;
                const premiumIds = [STRIPE_PLANS.premium.monthly, STRIPE_PLANS.premium.yearly];
                const proIds = [STRIPE_PLANS.pro.monthly, STRIPE_PLANS.pro.yearly];
                if (priceId && proIds.includes(priceId)) tier = 'pro_plus';
                else if (priceId && premiumIds.includes(priceId)) tier = 'premium';
                else if (sub.status === 'active') tier = 'premium'; 
            } else if (isWithinTrial) {
                tier = 'premium'; 
                trialActive = true;
            }
            setPlanTier(tier);
            setIsTrial(trialActive);

            const userProjects = await getUserProjects(user.uid);
            setProjectsList(userProjects);

            if (userProjects.length === 1) {
              setProject(userProjects[0]);
              setView('app');
            } else if (userProjects.length > 1) {
              setView('projects-list');
            } else {
              setView('welcome');
            }
          } else if (user && !user.emailVerified) {
            setView('auth');
          } else {
            setView('landing');
          }
        } catch (err: unknown) {
          console.error("[App] Auth state error:", err);
          setView('landing');
        } finally {
          setIsLoading(false);
        }
      });
    };

    initApp();
    return () => { if (unsubscribeAuth) unsubscribeAuth(); };
  }, []);

  // Monitorar mudança para view 'app' para disparar tutorial
  useEffect(() => {
    if (view === 'app' && !localStorage.getItem('tutorial_completed')) {
      const timer = setTimeout(() => setShowTutorial(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [view]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setProject(EMPTY_PROJECT);
      setProjectsList([]);
      setPlanTier('trial');
      setIsTrial(false);
      setView('landing');
      setActiveTab('dashboard');
    } catch (error) {
      console.error("[App] Logout error:", error);
    }
  };

  const handleSelectProject = (proj: Project) => {
    setProject(proj);
    setView('app');
    setActiveTab('dashboard');
  };

  const handleCreateNewProject = () => {
    setProject(EMPTY_PROJECT); 
    setView('onboarding-1');
  };

  const handleProjectCreatedOrUpdated = async (newProject: Project) => {
    if (auth.currentUser) {
      const updatedList = await getUserProjects(auth.currentUser.uid);
      setProjectsList(updatedList);
      const savedProject = updatedList.find(p => p.id === newProject.id) || newProject;
      setProject(savedProject);
      setView('app');
    }
  };

  const handleProjectJoined = async (deletedId?: string) => {
    if (!auth.currentUser) return;
    setIsLoading(true);
    try {
      const userProjects = await getUserProjects(auth.currentUser.uid);
      setProjectsList(userProjects);
      if (userProjects.length > 0) {
        if (deletedId && project.id === deletedId) {
            setView('projects-list');
        } else if (userProjects.length === 1) {
          setProject(userProjects[0]);
          setView('app');
        } else {
          setView('projects-list');
        }
      } else {
         setProject(EMPTY_PROJECT);
         setView('welcome');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    setActionLoading(true);
    try {
      const updatedExpenses = project.expenses.filter(e => e.id !== id);
      await updateUserProject(project.id, { expenses: updatedExpenses });
      setProject(prev => ({ ...prev, expenses: updatedExpenses }));
      setShowExpenseModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePhoto = async (id: string) => {
    if (!window.confirm("Deseja realmente excluir esta foto?")) return;
    setActionLoading(true);
    try {
      const updatedPhotos = project.photos.filter(p => p.id !== id);
      await updateUserProject(project.id, { photos: updatedPhotos });
      setProject(prev => ({ ...prev, photos: updatedPhotos }));
      setSelectedPhoto(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveExpense = async (expenseData: Partial<Expense>) => {
    setActionLoading(true);
    try {
      let updatedExpenses = [...project.expenses];
      if (editingExpenseId) {
        updatedExpenses = updatedExpenses.map(e => e.id === editingExpenseId ? { ...e, ...expenseData } as Expense : e);
      } else {
        const installments = expenseData.installments || 1;
        const baseAmount = expenseData.amount || 0;
        const installmentValue = baseAmount / installments;
        const baseDateString = expenseData.date || toLocalISODate(new Date());
        const [year, month, day] = baseDateString.split('-').map(Number);
        const newEntries: Expense[] = [];
        
        for (let i = 0; i < installments; i++) {
          const installmentDateObj = new Date(year, (month - 1) + i, day);
          const formattedPaymentDate = toLocalISODate(installmentDateObj);
          const suffix = installments > 1 ? ` (${i + 1}/${installments})` : "";
          
          newEntries.push({
            id: crypto.randomUUID(),
            description: (expenseData.description || 'Gasto') + suffix,
            amount: installmentValue,
            date: baseDateString,
            paymentDate: formattedPaymentDate,
            isPaid: i === 0 ? (expenseData.isPaid || false) : false,
            supplier: expenseData.supplier || 'N/A',
            stageId: expenseData.stageId || project.stages[0]?.id || '1',
            installments: 1,
            category: expenseData.category || ExpenseCategory.OTHERS,
            receiptUrl: expenseData.receiptUrl || '',
          });
        }
        updatedExpenses = [...newEntries, ...updatedExpenses];
      }
      await updateUserProject(project.id, { expenses: updatedExpenses });
      setProject(prev => ({ ...prev, expenses: updatedExpenses }));
      setShowExpenseModal(false);
      setEditingExpenseId(null);
    } catch (err: unknown) {
       console.error("[App] Save expense error:", err);
       alert("Erro ao salvar despesa.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSavePhoto = async (photoData: Partial<Photo>) => {
    if (planTier === 'trial' && !isProviderMode) {
      const photosThisMonth = project.photos.filter(p => {
          const d = new Date(p.date);
          return d.getMonth() === new Date().getMonth() && d.getFullYear() === new Date().getFullYear();
      }).length;
      if (photosThisMonth >= APP_CONFIG.MAX_FREE_PHOTOS_PER_MONTH) {
          setShowPhotoUploadModal(false);
          setTempPhotoUrl(null);
          setShowPricingModal(true);
          return;
      }
    }
    setActionLoading(true);
    try {
      const newPhoto: Photo = {
        id: crypto.randomUUID(),
        url: tempPhotoUrl || '',
        date: photoData.date || toLocalISODate(new Date()),
        description: photoData.description || '',
        room: photoData.room || 'Geral',
        stageId: photoData.stageId || project.stages[0]?.id || '',
        uploadedBy: auth.currentUser?.displayName || (isProviderMode ? 'Prestador' : 'Colaborador')
      };
      const updatedPhotos = [newPhoto, ...project.photos];
      await updateUserProject(project.id, { photos: updatedPhotos });
      setProject(prev => ({ ...prev, photos: updatedPhotos }));
      setShowPhotoUploadModal(false);
      setTempPhotoUrl(null);
    } catch (err: unknown) {
       console.error("[App] Save photo error:", err);
       alert("Erro ao salvar foto.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setActionLoading(true); 
      try {
        const compressedUrl = await compressImage(file, 1280, 0.8);
        setTempPhotoUrl(compressedUrl);
        setShowPhotoUploadModal(true);
      } catch (err: unknown) {
         console.error("[App] Compression error:", err);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handlePlanSelection = async (priceId: string) => {
    setIsLoading(true);
    try {
      await createCheckoutSession(priceId);
    } catch (error) {
      console.error("[App] Payment error:", error);
      alert("Erro ao iniciar pagamento.");
      setIsLoading(false);
    }
  };

  if (isLoading) return (
    <div className="min-h-screen mesh-gradient flex items-center justify-center">
      <div className="size-20 rounded-full border-4 border-emerald-200 border-t-emerald-600 animate-spin"></div>
    </div>
  );

  if (view === 'error-link') return (
    <div className="min-h-screen mesh-gradient flex flex-col items-center justify-center p-8 text-center">
       <div className="size-20 bg-rose-100 text-rose-600 rounded-3xl flex items-center justify-center mb-6"><LinkIcon size={40}/></div>
       <h1 className="text-3xl font-black uppercase text-slate-900 mb-2">Acesso Negado</h1>
       <p className="text-slate-500 font-bold mb-8">{globalError}</p>
       <button onClick={() => window.location.assign(window.location.origin)} className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest">Voltar para Home</button>
    </div>
  );

  if (view === 'landing') return <Landing onAuth={(p) => { setView('auth'); }} />;
  if (view === 'auth') return <Auth onBack={() => setView('landing')} onSuccess={() => handleProjectJoined()} />;
  if (view === 'welcome') return <Welcome onNext={() => setView('onboarding-1')} onProjectJoined={() => handleProjectJoined()} onBack={handleLogout} initialInviteCode={inviteCodeFromUrl} />;
  
  if (view === 'projects-list') return (
    <>
      <ProjectList projects={projectsList} onSelect={handleSelectProject} onNew={handleCreateNewProject} userName={auth.currentUser?.displayName || "Usuário"} planTier={planTier} onUpgrade={() => setShowPricingModal(true)} />
      {showPricingModal && <PricingModal onClose={() => setShowPricingModal(false)} onSelectPlan={handlePlanSelection} isLoading={isLoading} currentTier={planTier} isTrial={isTrial} />}
    </>
  );

  if (view === 'onboarding-1') return <ProjectSetup onNext={(data) => { setProject({ ...EMPTY_PROJECT, ...data, id: 'temp' }); setView('onboarding-2'); }} onBack={() => projectsList.length > 0 ? setView('projects-list') : setView('welcome')} planTier={planTier} onUpgrade={() => setShowPricingModal(true)} />;
  if (view === 'onboarding-2') return <OnboardingStep2 name={project.name} budget={project.initialBudget} onNext={(data) => { setProject({ ...project, ...data, stages: data.type === ProjectType.RENOVATION ? RENOVATION_STAGES : CONSTRUCTION_STAGES }); setView('onboarding-3'); }} onBack={() => setView('onboarding-1')} />;
  if (view === 'onboarding-3') return <OnboardingStep3 onNext={() => setView('onboarding-4')} onBack={() => setView('onboarding-2')} />;
  if (view === 'onboarding-4') return <OnboardingStep4 onComplete={() => setView('stages-setup')} onBack={() => setView('onboarding-3')} />;
  
  if (view === 'stages-setup') return (
    <>
      <StagesSetup 
        projectName={project.name} 
        budgetMode={project.budgetMode}
        initialStages={project.stages}
        initialBudget={project.initialBudget}
        isLoading={actionLoading}
        planTier={planTier}
        onUpgrade={() => setShowPricingModal(true)}
        onBack={() => project.id && project.id !== 'temp' ? setView('app') : setView('onboarding-4')} 
        onComplete={async (stages, newMode, generalBudgetFromSetup) => {
          setActionLoading(true);
          try {
            const sumOfStages = stages.reduce((acc, s) => acc + (s.expectedBudget || 0), 0);
            const updatedBudget = newMode === 'detailed' ? sumOfStages : (generalBudgetFromSetup ?? project.initialBudget);
            
            const finalProject = { ...project, stages, initialBudget: updatedBudget, budgetMode: newMode };

            if (auth.currentUser) {
              if (project.id && project.id !== 'temp') {
                 await updateUserProject(project.id, { stages, initialBudget: updatedBudget, budgetMode: newMode });
                 handleProjectCreatedOrUpdated(finalProject);
              } else {
                 const newId = await saveUserProject(auth.currentUser.uid, finalProject);
                 handleProjectCreatedOrUpdated({ ...finalProject, id: newId });
              }
            } else {
              setProject(finalProject);
              setView('app');
            }
          } finally {
            setActionLoading(false);
          }
        }} 
      />
    </>
  );

  return (
    <Layout activeTab={activeTab} setActiveTab={setActiveTab} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} projectName={project.name} projectLogo={project.logoUrl} onLogout={handleLogout} onSwitchProject={() => setView('projects-list')}>
      {/* Smart Notifications System - Inserido aqui para sobrepor a UI, mas dentro do contexto da obra */}
      {view === 'app' && project.id && (
        <SmartNotification 
          project={project} 
          onAddExpense={() => { setEditingExpenseId(null); setShowExpenseModal(true); }}
          onTakePhoto={() => fileInputRef.current?.click()}
          onViewReports={() => setActiveTab('stats')}
        />
      )}

      {activeTab === 'dashboard' && <DashboardView project={project} setActiveTab={setActiveTab} onNewExpense={() => { setEditingExpenseId(null); setShowExpenseModal(true); }} onEditExpense={(id) => { setEditingExpenseId(id); setShowExpenseModal(true); }} onEditBudget={() => setView('stages-setup')} />}
      {activeTab === 'expenses' && <ExpensesView project={project} onNewExpense={() => { setEditingExpenseId(null); setShowExpenseModal(true); }} onEditExpense={(id) => { setEditingExpenseId(id); setShowExpenseModal(true); }} />}
      {activeTab === 'photos' && <PhotoGallery project={project} onTakePhoto={() => fileInputRef.current?.click()} onViewPhoto={setSelectedPhoto} />}
      {activeTab === 'stats' && <StatsView project={project} currentUser={auth.currentUser?.displayName || "Usuário"} onEditStages={() => setView('stages-setup')} planTier={planTier} onUpgrade={() => setShowPricingModal(true)} />}
      {activeTab === 'settings' && <SettingsView project={project} setProject={setProject} onLogout={handleLogout} onCreateProject={handleCreateNewProject} onDeleteProject={handleProjectJoined} planTier={planTier} onUpgrade={() => setShowPricingModal(true)} projectsCount={projectsList.length} />}
      
      {showExpenseModal && <ExpenseModal project={project} editingExpenseId={editingExpenseId} onClose={() => setShowExpenseModal(false)} onSave={handleSaveExpense} onDelete={handleDeleteExpense} actionLoading={actionLoading} planTier={planTier} onUpgrade={() => setShowPricingModal(true)} />}
      {showPhotoUploadModal && <PhotoUploadModal tempPhotoUrl={tempPhotoUrl} project={project} onSave={handleSavePhoto} onClose={() => setShowPhotoUploadModal(false)} actionLoading={actionLoading} />}
      {showPricingModal && <PricingModal onClose={() => setShowPricingModal(false)} onSelectPlan={handlePlanSelection} isLoading={isLoading} currentTier={planTier} isTrial={isTrial} />}

      {showTutorial && <OnboardingTutorial onComplete={() => setShowTutorial(false)} />}

      {selectedPhoto && (
        <div className="fixed inset-0 z-[300] bg-slate-950/95 flex items-center justify-center p-6 animate-fadeIn" onClick={() => setSelectedPhoto(null)}>
           <div className="bg-white dark:bg-slate-950 w-full max-w-2xl rounded-[4rem] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="w-full aspect-square bg-black"><img src={selectedPhoto.url} className="w-full h-full object-contain" alt={selectedPhoto.description || selectedPhoto.room} /></div>
              <div className="p-10 space-y-6">
                 <div className="flex justify-between items-start">
                    <div><h2 className="text-2xl font-black uppercase">{selectedPhoto.room}</h2></div>
                    <button onClick={() => handleDeletePhoto(selectedPhoto.id)} className="text-red-500 p-3 rounded-2xl"><Trash size={20}/></button>
                 </div>
                 <p className="text-slate-600 dark:text-slate-300 font-bold italic leading-relaxed text-lg">"{selectedPhoto.description || "Sem comentários."}"</p>
              </div>
           </div>
        </div>
      )}
      <input type="file" accept="image/*" className="hidden" ref={fileInputRef} onChange={handlePhotoInputChange} />
    </Layout>
  );
};

export default App;
